export function compact(value: number, digits = 1): string {
  return new Intl.NumberFormat("en", {
    notation: "compact",
    maximumFractionDigits: digits
  }).format(value);
}

export function eth(value: number, digits = 2): string {
  return `${value.toLocaleString("en", { maximumFractionDigits: digits })} ETH`;
}

export function tradeTokenAmount(value: number, symbol: string): string {
  if (!Number.isFinite(value) || value <= 0) return `0 ${symbol}`;
  const formatted = value >= 100_000
    ? compact(value, 2)
    : value.toLocaleString("en-US", { maximumFractionDigits: value < 1 ? 6 : 2 });
  return `${formatted} ${symbol}`;
}

export function tradeEthAmount(value: number): string {
  if (!Number.isFinite(value) || value <= 0) return "0 ETH";
  const maximumFractionDigits = value < 0.01 ? 6 : value < 1 ? 4 : 3;
  return `${value.toLocaleString("en-US", { maximumFractionDigits })} ETH`;
}

export function marketCap(value: number | null): string {
  if (value === null || !Number.isFinite(value) || value <= 0) return "USD PENDING";
  if (value >= 100_000) return `$${compact(value, value >= 1_000_000 ? 2 : 1)} MCAP`;
  return `$${Math.round(value).toLocaleString("en-US")} MCAP`;
}

export function usd(value: number | null): string {
  if (value === null || !Number.isFinite(value) || value <= 0) return "USD pending";
  return `$${Math.round(value).toLocaleString("en-US")}`;
}

export function tokenPriceUsd(marketCapUsd: number | null, supply = 1_000_000_000): string {
  if (marketCapUsd === null || !Number.isFinite(marketCapUsd) || marketCapUsd <= 0 || supply <= 0) return "USD pending";
  return `$${(marketCapUsd / supply).toLocaleString("en-US", { maximumSignificantDigits: 6, maximumFractionDigits: 12 })}`;
}

export function compactUsd(value: number | null): string {
  if (value === null || !Number.isFinite(value) || value <= 0) return "USD pending";
  return `$${compact(value, value >= 1_000_000 ? 2 : 1)}`;
}

export function volumeUsdValue(value: number | null | undefined, volumeEth: number, marketCapUsd: number | null, fdvEth: number): number | null {
  const fallback = marketCapUsd && fdvEth > 0 ? volumeEth * (marketCapUsd / fdvEth) : null;
  return value ?? fallback;
}

export function volumeUsd(value: number | null | undefined, volumeEth: number, marketCapUsd: number | null, fdvEth: number): string {
  return usd(volumeUsdValue(value, volumeEth, marketCapUsd, fdvEth));
}

export function shortAddress(value: string): string {
  return `${value.slice(0, 6)}…${value.slice(-4)}`;
}

export function relativeTime(value: string): string {
  const delta = Math.max(0, Date.now() - new Date(value).getTime());
  const minutes = Math.floor(delta / 60_000);
  if (minutes < 1) return "now";
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  return `${Math.floor(hours / 24)}d`;
}

export function safeExternalUrl(value: string): string | null {
  if (!value) return null;
  try {
    const url = new URL(value);
    return url.protocol === "https:" ? url.toString() : null;
  } catch {
    return null;
  }
}

export function logoSeed(symbol: string): number {
  return [...symbol].reduce((total, char) => total + char.charCodeAt(0), 0) % 4;
}
